import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ReviewBar from '../../ReviewBar/ReviewBar';

import { sellProductFromStorage } from '../../../store/actions/sell/sellWizardActions';

import { valuesMultiply } from '../../../util/math/money';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import SectionLoader from '../../Loader/SectionLoader';
import {
  getWizardContent,
  setWizardContent,
} from '../../../util/helpers/wizardHelpers';
import useGtmHook from '../../../util/hooks/useGtmHook';
import { ERROR_TEXT, HAS_ERROR } from '../../../constants/sessionStorage';
import { PURCHASE } from '../../../constants/gtmEvents';
import Note from '../../Notes/Note';
import ModalBody from '../../Modal/ModalBody';
import ModalTitle from '../../Modal/ModalTitle';
import ModalButtons from '../../Modal/ModalButtons';
import AgreementCheckbox from '../../InputFields/AgreementCheckbox';
import ReviewList from '../../ReviewList/ReviewList';
import ThinMarketWarning from '../../ThinMarketWarning/ThinMarketWarning';
import { verifySellGtmHelper } from '../../../util/helpers/gtmHelper';
import CountdownTimer from '../../Common/Timers/CountdownTimer';
import SellCalculation from './SellCalculation';

const SellConfirm = ({
  handleBack,
  handleNext,
  handleRefreshTransactions,
  wizardData,
  setWizardData,
  product,
  unitPrice,
  setPricePerUnit,
  vault,
  tiers,
  reviewProduct,
  setHasError,
  setErrorText,
  account,
  isFractionalProduct,
  counter,
  unity,
  isLockedPrice,
  isProductPage,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { ecommerceGtmEvent } = useGtmHook();
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const isSegregated = product?.IsSegregated;
  const [reviewData, setReviewData] = useState([]);

  const handleError = (value) => {
    setWizardContent(HAS_ERROR, value);
    setHasError(value);

    if (getWizardContent(HAS_ERROR)) {
      const text = isFractionalProduct
        ? t('apiErrors.Order_FractionalOrder_PriceIsNotAvailable')
        : t('sellWizard.orderCouldNotBePlaced');
      setWizardContent(ERROR_TEXT, text);
      setErrorText(text);
    } else {
      const text = t('sellWizard.orderPlaced');
      setWizardContent(ERROR_TEXT, text);
      setErrorText(text);
    }
  };

  const handleConfirm = () => {
    const onRequestSuccess = () => {
      const handleRefreshTransactionAvailable = !!handleRefreshTransactions;

      if (handleRefreshTransactionAvailable) {
        handleRefreshTransactions();
      }
    };

    const handleGtm = () => {
      // gtmEvent(TRADE_EXECUTED, {
      //   AccountType: parseEnumType(accountTypeKeys, account.AccountType),
      //   TerminalPointType: 'Storage',
      //   DirectionType: 'Out',
      //   Symbol: reviewProduct.SymbolCode,
      //   OrderSideType: 'Sell',
      //   QuantityExecuted: reviewProduct.Quantity,
      //   OrderTotal: valuesMultiply(reviewProduct.Quantity, vault.PricePerUnit),
      // });

      verifySellGtmHelper({
        ecommerceGtmEvent,
        eventName: PURCHASE,
        reviewProduct,
        product,
        vault,
        account,
        orderSideType: 'sell',
      });
    };
    dispatch(
      sellProductFromStorage({
        reviewProduct,
        isSegregated,
        setIsLoading,
        handleNext,
        handleError,
        handleGtm,
        onRequestSuccess,
      }),
    );
  };

  const handleCheckboxCheck = () => {
    setIsCheckboxChecked(!isCheckboxChecked);
  };

  useEffect(() => {
    const dataForReview = [
      {
        label: isLockedPrice
          ? t('sellWizard.pricePerUnit')
          : t('sellWizard.estimatedPricePerUnit'),
        value: formatMoneyNumeral(wizardData?.calculation?.newPricePerUnit),
      },
      {
        label: t('sellWizard.quantityToSell'),
        value: reviewProduct?.Quantity,
      },
      {
        label: isLockedPrice
          ? t('sellWizard.metalsTotal')
          : t('sellWizard.estimatedMetalsTotal'),
        value: formatMoneyNumeral(
          valuesMultiply(
            reviewProduct?.Quantity,
            wizardData?.calculation?.newPricePerUnit,
          ),
        ),
      },
    ];
    setReviewData(dataForReview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLockedPrice, reviewProduct?.Quantity, tiers]);

  return (
    <SectionLoader isLoading={isLoading}>
      <ReviewBar title={t('sellWizard.reviewSale')} />
      <ModalBody>
        <ModalTitle>{product.ProductCaption}</ModalTitle>
        <ReviewList data={reviewData} />
        <AgreementCheckbox
          checked={isCheckboxChecked}
          onChange={handleCheckboxCheck}
          text={t('sellWizard.acknowledgeFinality')}
          marginBottom={16}
          marginTop={16}
          data-cy="checkbox-acknowledge-sales-final"
        />
        {!isLockedPrice && <Note text={t('sellWizard.note')} />}
        {product.HasThinMarket && <ThinMarketWarning />}
        <SellCalculation
          unitPrice={unitPrice}
          quantity={reviewProduct?.Quantity}
          wizardData={wizardData}
          setWizardData={setWizardData}
          tiers={tiers}
          barsArray={product?.Bars}
          isLockedPriceUser={isLockedPrice}
          setPricePerUnit={setPricePerUnit}
          isProductPage={isProductPage}
          isReviewModal
        />
        <CountdownTimer counter={counter} unity={unity} />
        <ModalButtons
          isHorizontal
          marginTop
          secondaryButtonProps={{
            onClick: handleBack,
            'data-cy': 'button-back',
          }}
          primaryButtonProps={{
            disabled: !isCheckboxChecked,
            onClick: handleConfirm,
            label: t('common.confirm'),
            'data-cy': 'button-confirm',
          }}
        />
      </ModalBody>
    </SectionLoader>
  );
};

SellConfirm.propTypes = {
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  handleRefreshTransactions: PropTypes.func,
  wizardData: PropTypes.shape({
    calculation: PropTypes.shape({
      estimatedOrderTotal: PropTypes.number,
      newPricePerUnit: PropTypes.number,
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
      IsCashOrderOnly: PropTypes.bool,
    }),
    storageProduct: PropTypes.shape({
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      Caption: PropTypes.string,
      ProductCaption: PropTypes.string,
    }),
  }),
  setWizardData: PropTypes.func,
  product: PropTypes.shape({
    ProductCaption: PropTypes.string,
    SymbolCode: PropTypes.string,
    HasThinMarket: PropTypes.bool,
    IsSegregated: PropTypes.bool,
    Locations: PropTypes.arrayOf(
      PropTypes.shape({
        PricePerUnit: PropTypes.number,
        SymbolCode: PropTypes.string,
      }),
    ),
    Bars: PropTypes.arrayOf(
      PropTypes.shape({
        BrandCode: PropTypes.string,
        SerialNumber: PropTypes.string,
        FineWeight: PropTypes.number,
        SequenceNumber: PropTypes.number,
      }),
    ),
  }),
  unitPrice: PropTypes.number,
  setPricePerUnit: PropTypes.func,
  vault: PropTypes.shape({
    SymbolCode: PropTypes.string,
    PricePerUnit: PropTypes.number,
  }),
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      HighBound: PropTypes.number,
      LowBound: PropTypes.number,
      PricePerOz: PropTypes.number,
      PricePerUnit: PropTypes.number,
    }),
  ),
  isFractionalProduct: PropTypes.bool,
  reviewProduct: PropTypes.shape({
    Quantity: PropTypes.number,
    SymbolCode: PropTypes.string,
    QuantityAmount: PropTypes.number,
    Type: PropTypes.string,
    UnitType: PropTypes.string,
  }),
  setHasError: PropTypes.func,
  setErrorText: PropTypes.func,
  account: PropTypes.shape({
    AccountType: PropTypes.number,
  }),
  isProductPage: PropTypes.bool,
  counter: PropTypes.number,
  unity: PropTypes.bool,
  isLockedPrice: PropTypes.bool,
};

export default SellConfirm;
